import axios from "axios";
import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Form } from "react-bootstrap";
import Select from "react-select";
import { MdExpandMore } from "react-icons/md";

import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import DataTable from "../../../components/ui/DataTable";
import CustomPagination from "../../../components/ui/CustomPagination";
import StandardizedDateInput from "../../../components/ui/StandardizedDateInput";
import { useAdminAuth } from "../../auth/AdminAuthProvider";

/**
 * The shell every Daily Loan report is rendered in.
 *
 * WHY ONE SHELL AND NOT THREE SCREENS
 *
 * The Business Loan, GP Loan and JLG reports are separate 400-line files that
 * repeat the same sidebar, header, filter bar, table and pagination, and each
 * repeats the filtering logic too. CLAUDE.md section 3 forbids that duplication
 * for report code, and says so because it already caused a production data bug:
 * the same defect existed three times because the same report existed three
 * times.
 *
 * The three DL reports differ only in their endpoint, their columns and their
 * title, so those are props. A fix to the filter bar or the export fixes all
 * three at once.
 *
 * WHY THERE IS NO ARITHMETIC HERE
 *
 * The monthly report screens call generateLoanSummaryForMember in the browser,
 * once per row, over every receipt of every loan. A DL loan has 100 to 125
 * receipts, so doing that here would ship the heap problem to the browser.
 *
 * Every figure on a DL report is already computed by the server - outstanding,
 * PAR, DPD, bucket, the lot - by one shared builder that all three endpoints
 * call. This screen only displays what it is given. That is also why the numbers
 * cannot disagree between two DL reports.
 *
 * Props:
 *   title       string   - the heading, e.g. "DL Master Report"
 *   endpoint    string   - the API path, e.g. "getDlMasterReport"
 *   columns     array    - DataTable columns, [{ label, render }]
 *   reportName  string   - the Excel export file name
 *   extraParams object   - optional query params for this report
 *   renderSummary function(rows) - optional block above the table
 */
const DlReportScreen = ({
  title,
  endpoint,
  columns,
  reportName,
  extraParams = {},
  renderSummary,
}) => {
  const { adminUser } = useAdminAuth();

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openSidebarToggle, setOpenSidebarToggle] = useState(
    window.innerWidth > 1000
  );
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedBranchFilter, setSelectedBranchFilter] = useState(null);
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState(null);

  const itemsPerPage = 20;
  const OpenSidebar = () => setOpenSidebarToggle(!openSidebarToggle);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // extraParams is an object literal at every call site, so a new identity on
  // every render. Serialising it keeps the effect from looping forever.
  const extraParamsKey = JSON.stringify(extraParams);

  const fetchReport = useCallback(async () => {
    if (!adminUser?.user?.id) return;
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/api/${endpoint}`, {
        params: {
          id: adminUser.user.id,
          ...(fromDate && { fromDate }),
          ...(toDate && { toDate }),
          ...JSON.parse(extraParamsKey),
        },
      });
      // The DL endpoints return a plain array when unpaginated, and { rows }
      // when paged. Accept either so the screen keeps working if pagination is
      // switched on later.
      const rows = Array.isArray(response.data)
        ? response.data
        : response.data?.rows || [];
      setReportData(rows);
      setError("");
    } catch (err) {
      // The endpoints refuse an unreadable date and a backwards window with a
      // message that says exactly what is wrong. Show it rather than a generic
      // failure.
      setError(
        err.response?.data?.error ||
          "Could not load the report. Please try again."
      );
      setReportData([]);
    } finally {
      setLoading(false);
    }
  }, [adminUser?.user?.id, endpoint, fromDate, toDate, extraParamsKey]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Branch and employee are filtered in the browser because the rows are already
  // here. The date window is NOT: it goes to the server, because on a collection
  // report it selects which collections are summed, which no amount of
  // client-side filtering can reproduce.
  const filterRows = (rows) => {
    let filtered = rows;

    if (selectedBranchFilter) {
      filtered = filtered.filter(
        (row) => row?.branchName === selectedBranchFilter.value
      );
    }
    if (selectedEmployeeFilter) {
      filtered = filtered.filter(
        (row) => row?.username === selectedEmployeeFilter.value
      );
    }
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((row) =>
        [row?.memberName, row?.customerId, row?.loanId, row?.phoneNumber]
          .map((value) => String(value || "").toLowerCase())
          .some((value) => value.includes(term))
      );
    }

    return filtered;
  };

  const allFilteredData = filterRows(reportData);
  const totalPages = Math.ceil(allFilteredData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const pageRows = allFilteredData.slice(
    indexOfLastItem - itemsPerPage,
    indexOfLastItem
  );

  const branchOptions = [...new Set(reportData.map((row) => row?.branchName))]
    .filter(Boolean)
    .map((name) => ({ value: name, label: name }));

  const employeeOptions = [...new Set(reportData.map((row) => row?.username))]
    .filter(Boolean)
    .map((name) => ({ value: name, label: name }));

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <h1 className="TableHeaderText w-100 text-start">{title}</h1>

            <button
              className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </span>
              <MdExpandMore size={22} />
            </button>

            <div
              className={`filter-container ${
                !showMobileFilters ? "mobile-hidden" : "mobile-visible"
              }`}
            >
              <Row>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                      value={searchTerm}
                      onChange={(event) => {
                        setSearchTerm(event.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={2} md={6}>
                  <Form.Group>
                    <Form.Label>From Date</Form.Label>
                    <StandardizedDateInput
                      value={fromDate}
                      onChange={(event) => {
                        setFromDate(event.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={2} md={6}>
                  <Form.Group>
                    <Form.Label>To Date</Form.Label>
                    <StandardizedDateInput
                      value={toDate}
                      onChange={(event) => {
                        setToDate(event.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={4} md={6}>
                  <Form.Label>Branch</Form.Label>
                  <Select
                    value={selectedBranchFilter}
                    onChange={(value) => {
                      setSelectedBranchFilter(value);
                      setCurrentPage(1);
                    }}
                    options={branchOptions}
                    isClearable
                    placeholder="Filter by Branch"
                    className="filter-dropdown"
                  />
                </Col>
                <Col xs={12} lg={4} md={6}>
                  <Form.Label>Employee</Form.Label>
                  <Select
                    value={selectedEmployeeFilter}
                    onChange={(value) => {
                      setSelectedEmployeeFilter(value);
                      setCurrentPage(1);
                    }}
                    options={employeeOptions}
                    isClearable
                    placeholder="Filter by Employee Name"
                    className="filter-dropdown"
                  />
                </Col>
              </Row>
            </div>
          </div>

          {error ? (
            <div className="alert alert-danger m-3" role="alert">
              {error}
            </div>
          ) : null}

          {renderSummary ? renderSummary(allFilteredData) : null}

          <div className="master-list-section">
            <div
              className="master-table-wrapper"
              style={{ padding: "0 15px" }}
            >
              {loading ? (
                <p className="text-center p-4">Loading...</p>
              ) : (
                <DataTable
                  columns={columns}
                  data={pageRows}
                  reportName={reportName}
                  excelData={allFilteredData}
                />
              )}
            </div>

            <div className="master-pagination-wrapper">
              <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                paginate={paginate}
                totalItems={allFilteredData.length}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DlReportScreen;
